import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import {
  Clock, CheckCircle, Loader2, ArrowRight, Calendar,
  MapPin, Phone, MessageCircle, Star, Camera,
  ChevronLeft, ChevronRight, ClipboardList
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import config from '../config';

export const ProjectTrackingPage = () => {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [contract, setContract] = useState(null);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchData();
  }, [contractId]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const [contractRes, progressRes] = await Promise.all([
        fetch(config.getApiUrl(`/marketplace/contracts/${contractId}/`), {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(config.getApiUrl(`/marketplace/contracts/${contractId}/progress/`), {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (contractRes.ok) {
        const data = await contractRes.json();
        setContract(data);
      }

      if (progressRes.ok) {
        const data = await progressRes.json();
        setProgress(data.results || data);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('خطا در دریافت اطلاعات');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteProject = async () => {
    if (!window.confirm('آیا از تکمیل این پروژه اطمینان دارید؟ پس از تایید، مبلغ نهایی تسویه خواهد شد.')) return;

    setCompleting(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(config.getApiUrl(`/marketplace/contracts/${contractId}/complete/`), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('پروژه با موفقیت تکمیل شد');
        navigate(`/installations/${contract?.installation_request?.id}`);
      } else {
        toast.error('خطا در تکمیل پروژه');
      }
    } catch (error) {
      toast.error('خطا در ارتباط با سرور');
    } finally {
      setCompleting(false);
    }
  };

  const handleSubmitReview = async (rating, comment) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(config.getApiUrl('/reviews/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          installer: contract?.bid?.installer?.id,
          rating,
          comment,
          contract: contractId
        })
      });

      if (response.ok) {
        toast.success('نظر شما با موفقیت ثبت شد');
        fetchData();
      } else {
        toast.error('خطا در ثبت نظر');
      }
    } catch (error) {
      toast.error('خطا در ارتباط با سرور');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'in_progress': return <Clock className="w-5 h-5 text-blue-500" />;
      case 'completed': return <CheckCircle className="w-5 h-5 text-green-500" />;
      default: return <Loader2 className="w-5 h-5 text-gray-400" />;
    }
  };

  const stageNames = {
    initial_site_visit: 'بازدید اولیه',
    roof_preparation: 'آماده‌سازی سقف',
    panel_installation: 'نصب پنل‌ها',
    wiring: 'سیم‌کشی',
    inverter_installation: 'نصب اینورتر',
    testing: 'تست و راه‌اندازی',
    final_handover: 'تحویل نهایی'
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">پروژه یافت نشد</h2>
          <Link to="/installations" className="mt-4 text-blue-600">بازگشت</Link>
        </div>
      </div>
    );
  }

  const isCustomer = contract.installation_request?.customer?.id === user?.id;
  const isInstaller = contract.bid?.installer?.user?.id === user?.id;
  const isCompleted = contract.status === 'completed';

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-5xl">
        {/* Header */}
        <div className="mb-6">
          <Link to={isCustomer ? "/installations" : "/installer/contracts"} className="text-blue-600 hover:text-blue-800 flex items-center gap-1">
            <ArrowRight className="w-4 h-4" /> بازگشت
          </Link>
        </div>
        {isInstaller && contract.status === 'in_progress' && (
          <div className="mt-4 flex justify-end">
            <Link
              to={`/installer/progress/${contractId}`}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center gap-2"
            >
              <ClipboardList className="w-4 h-4" />
              ثبت گزارش پیشرفت
            </Link>
          </div>
        )}

        {/* Project Header */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex justify-between items-start flex-wrap gap-4">
            <div>
              <h1 className="text-2xl font-bold">پروژه #{contract.installation_request?.id}</h1>
              <p className="text-gray-500 mt-1">
                {contract.installation_request?.address}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {getStatusIcon(contract.status)}
              <span className="font-medium">
                {contract.status === 'in_progress' ? 'در حال اجرا' : 'تکمیل شده'}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">مبلغ قرارداد</p>
              <p className="font-bold text-blue-600">{contract.bid?.proposed_cost?.toLocaleString()} تومان</p>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">پیشرفت کلی</p>
              <p className="font-bold">{progress.percentage || 0}%</p>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">مراحل تکمیل شده</p>
              <p className="font-bold">{progress.filter(p => p.completed).length || 0}/{progress.length || 0}</p>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">نصاب</p>
              <p className="font-medium">{contract.bid?.installer?.company_name}</p>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-lg font-bold mb-4">پیشرفت پروژه</h2>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-green-600 h-3 rounded-full transition-all duration-500"
              style={{ width: `${progress.percentage || 0}%` }}
            />
          </div>
          <p className="text-center text-sm text-gray-500 mt-2">{progress.percentage || 0}% تکمیل شده</p>
        </div>

        {/* Timeline */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-lg font-bold mb-4">مراحل انجام کار</h2>
          <div className="relative">
            <div className="absolute top-0 bottom-0 right-5 w-0.5 bg-gray-200"></div>
            <div className="space-y-6">
              {progress.map((stage, idx) => (
                <div key={idx} className="relative flex gap-4">
                  <div className="relative z-10">
                    {stage.completed ? (
                      <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-white" />
                      </div>
                    ) : (
                      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                        <Clock className="w-5 h-5 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 pb-6">
                    <div className="flex justify-between items-start flex-wrap gap-2">
                      <h3 className="font-bold">{stageNames[stage.stage] || stage.stage}</h3>
                      {stage.completed_at && (
                        <span className="text-xs text-gray-400">
                          {new Date(stage.completed_at).toLocaleDateString('fa-IR')}
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 text-sm mt-1">{stage.description}</p>
                    {stage.images && stage.images.length > 0 && (
                      <div className="flex gap-2 mt-3">
                        {stage.images.slice(0, 3).map((img, imgIdx) => (
                          <button
                            key={imgIdx}
                            onClick={() => setSelectedImage(img)}
                            className="w-16 h-16 rounded-lg overflow-hidden border cursor-pointer hover:opacity-80"
                          >
                            <img src={img} alt="" className="w-full h-full object-cover" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Contact Installer */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-lg font-bold mb-4">ارتباط با نصاب</h2>
          <div className="flex flex-wrap gap-4">
            <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              <Phone className="w-4 h-4" />
              تماس تلفنی
            </button>
            <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              <MessageCircle className="w-4 h-4" />
              پیام در واتساپ
            </button>
          </div>
        </div>

        {/* Complete Project (for customer) */}
        {isCustomer && !isCompleted && contract.status === 'in_progress' && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h2 className="text-lg font-bold mb-4 text-green-600">تکمیل پروژه</h2>
            <p className="text-gray-600 mb-4">
              در صورت اطمینان از انجام کامل کار، می‌توانید پروژه را تکمیل و تسویه نهایی را انجام دهید.
            </p>
            <button
              onClick={handleCompleteProject}
              disabled={completing}
              className="bg-green-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              {completing ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
              تایید تکمیل پروژه
            </button>
          </div>
        )}

        {/* Review Section (for customer after completion) */}
        {isCustomer && isCompleted && !contract.has_review && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-bold mb-4">ثبت نظر و امتیاز</h2>
            <div className="flex items-center gap-2 mb-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => handleSubmitReview(star, prompt('نظر خود را بنویسید:'))}
                  className="text-2xl hover:scale-110 transition"
                >
                  <Star className={`w-6 h-6 ${star <= (progress.rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                </button>
              ))}
            </div>
            <p className="text-gray-500 text-sm">با ثبت نظر و امتیاز، به بهبود کیفیت خدمات کمک کنید</p>
          </div>
        )}

        {/* Image Modal */}
        {selectedImage && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => setSelectedImage(null)}>
            <img src={selectedImage} alt="" className="max-w-[90vw] max-h-[90vh] object-contain" />
            <button className="absolute top-4 right-4 text-white text-2xl" onClick={() => setSelectedImage(null)}>✕</button>
          </div>
        )}
      </div>
    </div>
  );
};
